/**
 * The portal's branded header: the studio logo (home link), the client's
 * name, the language switch, light / dark, and sign out. No studio nav.
 */

import { LogOut, Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/brand';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks';
import { useTheme } from '@/providers/ThemeProvider';

import { PortalLanguageMenu } from './PortalLanguageMenu';

export function PortalHeader({ clientName }: { clientName: string | null }) {
  const { t } = useTranslation('portal');
  const { logout } = useAuth();
  const { setTheme, resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const themeLabel = dark ? t('layout.lightMode') : t('layout.darkMode');
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <Link to="/portal" aria-label={t('layout.homeLink')} className="flex min-w-0 items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <BrandLogo decorative className="w-24 shrink-0 sm:w-32" />
        {clientName ? <span className="hidden truncate border-l border-border pl-3 text-sm text-muted-foreground sm:inline">{clientName}</span> : null}
      </Link>
      <div className="flex shrink-0 items-center gap-1">
        <PortalLanguageMenu />
        <Button variant="ghost" size="icon-sm" onClick={() => setTheme(dark ? 'light' : 'dark')} aria-label={themeLabel} title={themeLabel}>
          {dark ? <Sun aria-hidden /> : <Moon aria-hidden />}
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={logout} aria-label={t('layout.signOut')} title={t('layout.signOut')}>
          <LogOut aria-hidden />
        </Button>
      </div>
    </div>
  );
}
